import { ICheckbox, IField } from 'app/services/api5-service/api.interface';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { ISP_TYPOGRAPHY_VAR } from 'common/colors';
import { getDesc } from 'common/dynamic-form/wrappers/field-base/description/description.utils';
import { getLabel } from 'common/dynamic-form/wrappers/field-base/label/label.utils';
import { FieldSettings } from 'common/dynamic-form/wrappers/field-base/model';
import { DocHelper } from 'utils/dochelper';

import { CheckboxProps } from './model/checkbox-props.interface';

import {
  ISPFormState,
  ISPFieldConfig,
  ISPFieldType,
  ISPFieldWrapper,
} from '../../model';

export interface CheckboxCaptions {
  captionOn: string;
  captionOff: string;
  fieldSettings: FieldSettings;
}

/**
 * Get captions and their displaying info for checkbox
 *
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getCheckboxCaptions(
  field: IField,
  state: ISPFormState,
): CheckboxCaptions {
  const captionOn = field.$noname
    ? ''
    : DocHelper.getMessage(`${field.$name}_on`, state.docSubject.value);

  const captionOff = field.$noname
    ? ''
    : DocHelper.getMessage(`${field.$name}_off`, state.docSubject.value);

  const label = getLabel(field, state.docSubject.value);
  const desc = getDesc(field, state)?.msg;
  const displayLabel = Boolean(label) && Boolean(desc);
  const captionOnOff = desc || label;

  const fieldSettings: FieldSettings = {
    hideLabel: !displayLabel,
    hintPlace: displayLabel ? 'label' : 'field',
  };

  return {
    captionOn: captionOn || captionOnOff,
    captionOff: captionOff || captionOnOff,
    fieldSettings,
  };
}

/**
 * Get stream of captions for checkbox
 *
 * @param field
 * @param state
 */
export function getCheckboxCaptions$(
  field: IField,
  state: ISPFormState,
): Observable<CheckboxCaptions> {
  return state.docSubject.pipe(
    map(() => getCheckboxCaptions(field, state)),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );
}

/**
 * Get props for checkbox field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getCheckboxProps(
  control: ICheckbox,
  field: IField,
  state: ISPFormState,
): CheckboxProps {
  const captions$ = getCheckboxCaptions$(field, state);

  return {
    originalControl: control,
    originalField: field,
    fieldSettings$: captions$.pipe(
      map(({ fieldSettings }) => ({
        labelSize: '1.17em',
        labelWeight: `var(${ISP_TYPOGRAPHY_VAR.bold})`,
        labelOffset: '1em',
        // hide desc in field component. Checkbox display it by it's own rules
        hideDesc: true,
        ...fieldSettings,
      })),
    ),
    toggle: control.$type === 'toggle',
    labelPosition: control.$msg_side,
    captions$,
    colorOn: control.$color_on,
    colorOff: control.$color_off,
    isMixed$: state.mixedService.isControlMixed$(
      control,
      ISPFieldType.Checkbox,
    ),
  };
}
/**
 * Get config for checkbox field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getCheckboxConfig(
  control: ICheckbox,
  field: IField,
  state: ISPFormState,
): ISPFieldConfig<ISPFieldType.Checkbox> {
  const props = getCheckboxProps(control, field, state);

  const config: ISPFieldConfig<ISPFieldType.Checkbox> = {
    key: control.$name,
    type: ISPFieldType.Checkbox,
    wrappers: [ISPFieldWrapper.FieldBase, ISPFieldWrapper.ValidationError],
    props,
    modelOptions: {
      updateOn: 'change',
    },
  };

  return config;
}
